import { PaperPlaneRightIcon } from '@phosphor-icons/react';
import { useCallback, useId, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from '@/components/ui/Button';
import InputField from '@/components/ui/Input';

export const GROUPS_DIRECTORY_QUESTION_PREFIX = 'With respect to my groups';
export const GROUP_OVERVIEW_QUESTION_PREFIX = 'With respect to this group';

export function prefixGroupsQuestion(prefix: string, question: string): string {
  return `${prefix}: ${question.trim()}`;
}

export const OVERVIEW_FOLLOW_UP_BUTTON_CLASS =
  'min-w-0 shrink overflow-hidden whitespace-nowrap [&_span:last-child]:min-w-0 [&_span:last-child]:truncate';

export default function GroupsOverviewAskInput({
  prefix,
  onSend,
  disabled = false,
  disabledReason,
  placeholder = 'Ask a question…',
  className
}: {
  prefix: string;
  onSend: (prompt: string) => void;
  disabled?: boolean;
  disabledReason?: string;
  placeholder?: string;
  className?: string;
}) {
  const inputId = useId();
  const [question, setQuestion] = useState('');
  const trimmed = question.trim();
  const canSend = !disabled && trimmed.length > 0;

  const submit = useCallback(() => {
    if (!canSend) return;
    onSend(prefixGroupsQuestion(prefix, trimmed));
    setQuestion('');
  }, [canSend, onSend, prefix, trimmed]);

  return (
    <form
      className={twMerge('flex w-[200px] min-w-[140px] shrink-0 items-center gap-1', className)}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        Ask a question about groups
      </label>
      <InputField
        id={inputId}
        type="text"
        fullWidth
        value={question}
        disabled={disabled}
        placeholder={placeholder}
        wrapperClassName="!h-[28px] px-2 py-0 text-xs"
        onChange={(event) => setQuestion(event.target.value)}
      />
      <Button
        type="submit"
        variant="primary"
        size="sm"
        icon={PaperPlaneRightIcon}
        aria-label="Send question"
        disabled={!canSend}
        disabledReason={disabled ? disabledReason : trimmed.length === 0 ? 'Enter a question to send' : undefined}
      />
    </form>
  );
}
